import { RACSearchDropdown } from '@rentacenter/racstrap';
import { get } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  ClassificationsInputPlaceholder,
  emptyField,
  PoFormField,
} from '../../constants/constants';
import { ClassificationType } from '../../domain/PurchaseOrder/ClassificationType';
import { PoStatus } from '../../domain/PurchaseOrder/PoStatus';
import { SelectedFieldOption } from '../../types/types';
import { filterById, populateSelectByChild } from '../../utils/utils';
import {
  useClassificationsDispatch,
  useClassificationsState,
} from './context/Provider';
import { getClassificationFieldName } from './context/fieldNames';
import { ClassificationsComponentProps } from './context/types';

export const classificationsTypeTestId = 'classificationsTypeComponent';

// eslint-disable-next-line
export const ClassificationsType = (props: ClassificationsComponentProps) => {
  const { name, label, purchaseOrder, qrScannerOutput, showErrorMessage } =
    props;
  const { errors, setValue, getValues } = useFormContext();
  const { initialTypes, types, startWithDepartment, isBracketSelected } =
    useClassificationsState();
  const dispatch = useClassificationsDispatch();
  const [itemValue, setItemValue] = useState(emptyField);
  const [isViewMode, setIsViewMode] = useState(false);
  const getFormFieldName = useCallback(
    (fieldName: PoFormField) => getClassificationFieldName(name, fieldName),
    [name]
  );

  useEffect(
    // eslint-disable-next-line
    () => {
      const poItemType = purchaseOrder && purchaseOrder.itemType;
      const optionTypes = types && types.length ? types : initialTypes || [];
      const selectedType: {
        name: string;
        id: string;
        subTypes: [];
      } | null = poItemType ? filterById(optionTypes, poItemType)[0] : null;

      if (types.length === 1) {
        setItemValue({ value: types[0].id, label: types[0].name });
        dispatch({
          type: ClassificationType.SET_SINGLE_SUBTYPE,
          payload: types[0].subTypes,
        });
        selectedType &&
          dispatch({
            type: ClassificationType.SET_SINGLE_SUBTYPE,
            payload: selectedType.subTypes,
          });
      } else {
        if (startWithDepartment) {
          setItemValue(emptyField);
          dispatch({
            type: ClassificationType.CLEAR_SUBTYPES,
            payload: [],
          });
        }
      }
      if (poItemType && !startWithDepartment && !isBracketSelected) {
        setItemValue({
          value: (selectedType && selectedType.id) || '',
          label:
            (selectedType && selectedType.name) ||
            ClassificationsInputPlaceholder,
        });
      }
      if (startWithDepartment && selectedType && isBracketSelected) {
        dispatch({
          type: ClassificationType.SET_TYPES,
          payload: selectedType.subTypes,
        });
      }

      if (qrScannerOutput) {
        const selectedItem = optionTypes?.find(
          (o) => o.id == qrScannerOutput.typeCode
        );
        setItemValue({
          value: selectedItem?.id
            ? selectedItem?.id
            : qrScannerOutput?.typeCode
            ? qrScannerOutput.typeCode
            : '',
          label: selectedItem?.name
            ? selectedItem?.name
            : qrScannerOutput?.type
            ? qrScannerOutput?.type
            : '',
        });
        setValue(
          getFormFieldName(PoFormField.ItemType),
          selectedItem?.id ? selectedItem?.id : qrScannerOutput?.typeCode,
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
        setIsViewMode(
          qrScannerOutput?.isScannedCategoryKept
            ? qrScannerOutput?.isScannedCategoryKept
            : false
        );
        if (!qrScannerOutput.departmentNumber) {
          setItemValue(emptyField);
          dispatch({
            type: ClassificationType.CLEAR_SUBTYPES,
            payload: [],
          });
        }
        // }
      }
    },
    // eslint-disable-next-line
    [types, dispatch, startWithDepartment, initialTypes, isBracketSelected,qrScannerOutput]
  );

  // Flag if dashboard-sent value is not found in classification options
  useEffect(() => {
    if (qrScannerOutput) return;
    const optionTypes = types?.length ? types : initialTypes || [];
    if (!optionTypes.length) return;
    const prefillValue = purchaseOrder?.itemType;
    if (!prefillValue) return;
    const resolved = optionTypes.some((t: any) => t.id == prefillValue);
    setTimeout(
      () => setValue(name + '__unresolved', resolved ? '' : 'true'),
      0
    );
    // eslint-disable-next-line
  }, [types, initialTypes, qrScannerOutput]);

  useEffect(
    () => {
      const poItemType = purchaseOrder && purchaseOrder.itemType;
      const poStartClassificationsSelect =
        purchaseOrder && purchaseOrder.startClassificationsSelect;

      // eslint-disable-next-line
      const optionTypes = types && types.length ? types : initialTypes || [];
      const selectedType: {
        name: string;
        id: string;
        subTypes: [];
      } | null = poItemType ? filterById(optionTypes, poItemType)[0] : null;

      if (
        poStartClassificationsSelect === 'itemType' &&
        !startWithDepartment &&
        selectedType
      ) {
        dispatch({
          type: ClassificationType.SET_TYPES_AS_PARENT,
          payload: selectedType,
        });
      }
    },
    // eslint-disable-next-line
    [dispatch, types, startWithDepartment, initialTypes]
  );

  const startClassificationsSelect = getValues(
    getFormFieldName(PoFormField.StartClassificationsSelect)
  );

  const handleChange = useCallback(
    // eslint-disable-next-line
    (selected: SelectedFieldOption) => {
      const { value, label, subTypes } = selected;

      setItemValue({ value, label });
      setValue(name, value, { shouldValidate: true, shouldDirty: true });

      startClassificationsSelect !== PoFormField.Department &&
        setValue(
          getFormFieldName(PoFormField.StartClassificationsSelect),
          PoFormField.ItemType
        );

      if (!startWithDepartment) {
        dispatch({
          type: ClassificationType.SET_TYPES_AS_PARENT,
          payload: selected,
        });
        if (purchaseOrder && purchaseOrder.itemType !== value) {
          purchaseOrder.itemSubType = '';
          purchaseOrder.department = '';
          purchaseOrder.subDepartment = '';
          purchaseOrder.bracket = '';
        }

        // eslint-disable-next-line
        let subTypes = selected.subTypes;
        let departments = subTypes[0].departments;
        let subDepartments = departments[0].subDepartments;
        let brackets = subDepartments[0].brackets;

        if (subTypes && subTypes.length === 1) {
          setValue(getFormFieldName(PoFormField.ItemSubType), subTypes[0].id, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } else {
          departments = [];
          subDepartments = [];
          brackets = [];
          setValue(getFormFieldName(PoFormField.ItemSubType), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }

        if (departments && departments.length === 1) {
          setValue(
            getFormFieldName(PoFormField.Department),
            departments[0].id,
            {
              shouldValidate: true,
              shouldDirty: true,
            }
          );
        } else {
          departments = [];
          subDepartments = [];
          brackets = [];
          setValue(getFormFieldName(PoFormField.Department), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }

        if (subDepartments.length === 1) {
          setValue(
            getFormFieldName(PoFormField.SubDepartment),
            subDepartments[0].id,
            {
              shouldValidate: true,
              shouldDirty: true,
            }
          );
        } else {
          departments = [];
          subDepartments = [];
          brackets = [];
          setValue(getFormFieldName(PoFormField.SubDepartment), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }

        if (brackets && brackets.length === 1) {
          setValue(getFormFieldName(PoFormField.Bracket), brackets[0].id, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } else {
          departments = [];
          subDepartments = [];
          brackets = [];
          setValue(getFormFieldName(PoFormField.Bracket), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }
      }

      if (startWithDepartment) {
        dispatch({
          type: ClassificationType.SET_TYPES,
          payload: subTypes,
        });
        if (subTypes.length === 1) {
          setValue(getFormFieldName(PoFormField.ItemSubType), subTypes[0].id, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } else {
          setValue(getFormFieldName(PoFormField.ItemSubType), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }
      }
    },
    [
      dispatch,
      getFormFieldName,
      startClassificationsSelect,
      name,
      purchaseOrder,
      setValue,
      startWithDepartment,
    ]
  );

  const typeOptions = useCallback(() => {
    return types.length > 0
      ? populateSelectByChild(types, 'subTypes')
      : populateSelectByChild(initialTypes, 'subTypes');
  }, [initialTypes, types]);

  useEffect(
    // eslint-disable-next-line
    () => {
      setIsViewMode(
        purchaseOrder
          ? purchaseOrder?.poStatus === PoStatus.SUBMITTED ||
              purchaseOrder.poStatus === PoStatus.OPEN_CANCELLED
          : false
      );
    },
    [purchaseOrder]
  );

  return useMemo(
    () => (
      <Controller
        name={name}
        rules={{ required: true }}
        render={({ name }) => (
          <RACSearchDropdown
            name={name}
            inputLabel={label}
            options={typeOptions() || []}
            OnChange={handleChange}
            required
            disabled={isViewMode || (startWithDepartment && !types.length)}
            value={itemValue}
            errormessage={
              showErrorMessage && get(errors, name)
                ? `${label} is required`
                : ''
            }
          />
        )}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isViewMode,
      itemValue,
      errors,
      label,
      name,
      purchaseOrder,
      showErrorMessage,
      startWithDepartment,
      typeOptions,
      types.length,
    ]
  );
};
